import {
  useGetCodebooksQuery,
  useGetCompanyCustomerInfoQuery,
} from "../../../common/queries";
import {
  AsyncSelectInput,
  DateInput,
  SelectInput,
  useAuth,
} from "../../../components";
import LabeledFormControl from "../../../components/forms/LabeledFormControl";
import { SelectItem } from "../../../components/select/Select";
import { oneLineAddress } from "../../../helpers/addressesHelpers";
import { useCustomTranslation } from "../../../helpers/translateHelpers";
import { useValidators } from "../../../helpers/validation";
import { RequestCreateSubFormType } from "./RequestCreateFormWrapper";
import { defaultSelectQuery } from "../../../helpers/tableHelpers";
import { loadServiceObjectsForSelect } from "../../../helpers/serviceObjectHelper";

interface RequestOrderFieldsProps {
  setVisibleSubForm: (type: RequestCreateSubFormType) => void;
  customAddressItem?: SelectItem;
  customContactPerson?: SelectItem;
  hideServiceObjectInput?: boolean;
}

const RequestOrderFields = ({
  setVisibleSubForm,
  customAddressItem,
  customContactPerson,
  hideServiceObjectInput,
}: RequestOrderFieldsProps) => {
  const { tRequest, tInput } = useCustomTranslation();
  const { user } = useAuth();

  const validators = useValidators();
  const codebooksQuery = useGetCodebooksQuery();
  const companyAndCustomerQuery = useGetCompanyCustomerInfoQuery();

  return (
    <>
      <LabeledFormControl label={tRequest("requested_date")}>
        <DateInput
          name="requestedDate"
          placeholder={tRequest("requested_date_placeholder")}
        />
      </LabeledFormControl>
      <LabeledFormControl isRequired label={tRequest("category")}>
        <SelectInput
          name="category"
          placeholder={tRequest("category_placeholder")}
          items={
            codebooksQuery.data?.orderRequestCategories.map((category) => ({
              label: category.name,
              value: category.id,
            })) ?? []
          }
          required
          options={{ required: validators.required }}
        />
      </LabeledFormControl>
      <LabeledFormControl isRequired label={tRequest("address")}>
        <SelectInput
          name="address"
          placeholder={tRequest("address_placeholder")}
          items={
            customAddressItem
              ? [
                  customAddressItem,
                  ...(companyAndCustomerQuery.data?.customer.addresses.map(
                    (address) => ({
                      label: oneLineAddress(address),
                      value: address.id,
                    }),
                  ) ?? []),
                ]
              : (companyAndCustomerQuery.data?.customer.addresses.map(
                  (address) => ({
                    label: oneLineAddress(address),
                    value: address.id,
                  }),
                ) ?? [])
          }
          required
          options={{ required: validators.required }}
          newOption={{
            label: tInput("select_new_address"),
            onClick: () => setVisibleSubForm("AddressSubForm"),
          }}
        />
      </LabeledFormControl>
      <LabeledFormControl isRequired label={tRequest("contact_person")}>
        <SelectInput
          name="contactPerson"
          placeholder={tRequest("contact_person_placeholder")}
          items={
            customContactPerson
              ? [
                  customContactPerson,
                  ...(companyAndCustomerQuery.data?.customer.contactPeople.map(
                    (person) => ({
                      label: person.fullName,
                      value: person.id,
                    }),
                  ) ?? []),
                ]
              : (companyAndCustomerQuery.data?.customer.contactPeople.map(
                  (person) => ({
                    label: person.fullName,
                    value: person.id,
                  }),
                ) ?? [])
          }
          required
          options={{ required: validators.required }}
          newOption={{
            label: tInput("select_new_contact_person"),
            onClick: () => setVisibleSubForm("ContactPersonSubForm"),
          }}
        />
      </LabeledFormControl>
      {!hideServiceObjectInput && (
        <LabeledFormControl label={tRequest("service_object")}>
          <AsyncSelectInput
            name="serviceObjects"
            placeholder={tRequest("service_object_placeholder")}
            loadOptions={(phrase, callback) =>
              loadServiceObjectsForSelect(
                {
                  ...defaultSelectQuery,
                  customerId: user?.customerId,
                  searchPhrase: phrase,
                },
                callback,
              )
            }
            isMulti
            multivalueTagLabelKey="selected_service_objects"
            // Currently we do not support to create a service object inside request
            // newOption={{
            //   label: tInput("select_new_service_object"),
            //   onClick: () =>
            //     props.setVisibleSubForm("ServiceObjectSubForm"),
            // }}
          />
        </LabeledFormControl>
      )}
    </>
  );
};

export default RequestOrderFields;
